import type {
  FormBlocks,
  FormMessages,
  FormTheme,
} from '@quillforms/types';
import { isValidPhoneNumber } from 'utils/inputEvent.util';
import type { IBusinessCard } from '../types/businessInterface';

function isLooseValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export const BUSINESS_CARD_QUILL_FORM_ID = 71001;

/** Visual tuning aligned with Quill “job application” style demos (full-step flow, progress, strong type). */
export const BUSINESS_CARD_QUILL_THEME: Partial<FormTheme> = {
  font: 'Open Sans',
  typographyPreset: 'lg',
  backgroundColor: '#ffffff',
  questionsColor: '#243841',
  questionsLabelFont: 'Open Sans',
  questionsDescriptionFont: 'Open Sans',
  answersColor: '#0077b8',
  buttonsFontColor: '#ffffff',
  buttonsBgColor: '#0077b8',
  buttonsBorderRadius: 8,
  buttonsBorderWidth: 0,
  buttonsBorderColor: '#0077b8',
  progressBarFillColor: '#00b1b0',
  progressBarBgColor: '#e3e8ec',
  errorsFontColor: '#ffffff',
  errorsBgColor: '#db4128',
  formFooterBgColor: {
    lg: '#ffffff',
    sm: '#f8fafb',
  },
};

export const BUSINESS_CARD_QUILL_MESSAGES: Partial<FormMessages> = {
  'label.submitBtn': 'Save contact',
  'label.skip': 'Skip',
};

export const BUSINESS_CARD_QUILL_BLOCKS: FormBlocks = [
  {
    id: 'welcome-intro',
    name: 'welcome-screen',
    attributes: {
      layout: 'stack',
      label: 'Add a business contact',
      description:
        'A quick intake to capture who you met and how to follow up.',
      buttonText: 'Get started',
    },
  },
  {
    id: 'name',
    name: 'short-text',
    attributes: {
      label: 'Name',
      required: true,
      placeholder: 'Full name',
    },
  },
  {
    id: 'organization',
    name: 'short-text',
    attributes: {
      label: 'Organization',
      required: false,
      placeholder: 'Company or organization',
    },
  },
  {
    id: 'title',
    name: 'short-text',
    attributes: {
      label: 'Title',
      required: false,
      placeholder: 'Job title',
    },
  },
  {
    id: 'email',
    name: 'email',
    attributes: {
      label: 'Email',
      required: false,
      placeholder: 'you@example.com',
      description:
        'Optional — leave blank or use Skip if you do not have it.',
    },
  },
  {
    id: 'phoneNumber',
    name: 'custom-phone-number',
    attributes: {
      label: 'Phone number',
      required: false,
      description:
        'Optional — leave blank or use Skip if you do not have it.',
    },
  },
  {
    id: 'website',
    name: 'short-text',
    attributes: {
      label: 'Website',
      required: false,
      placeholder: 'https://example.com',
      description:
        'Optional — leave blank or use Skip if you do not have it. Any text is fine if URL format is unknown.',
    },
  },
  {
    id: 'date',
    name: 'custom-date-picker',
    attributes: {
      label: 'Date',
      required: false,
      description: 'When did you meet this person?',
    },
  },
  {
    id: 'notes',
    name: 'long-text',
    attributes: {
      label: 'Notes',
      required: false,
      description: 'What is something memorable about this person?',
      placeholder: 'Optional',
    },
  },
  {
    id: 'submit-confirm',
    name: 'statement',
    attributes: {
      label: "You're all set!",
      description:
        'Review complete. Click the button below to save this contact.',
      buttonText: 'Save contact',
      quotationMarks: false,
    },
  },
];

/** First question after the welcome screen — autofocus target when the user taps Get started. */
export const BUSINESS_CARD_FIRST_FIELD_BLOCK_ID =
  BUSINESS_CARD_QUILL_BLOCKS.find(
    b => b.name !== 'welcome-screen' && b.name !== 'thankyou-screen',
  )?.id ?? 'name';

/** Steps with `required: false` — form footer Skip (same order as `BUSINESS_CARD_QUILL_BLOCKS`). */
export const BUSINESS_CARD_OPTIONAL_FIELD_BLOCK_IDS: string[] =
  BUSINESS_CARD_QUILL_BLOCKS.filter(
    block => block.attributes?.required === false,
  ).map(block => block.id);

type QuillAnswerEntry = { value?: unknown } | undefined;

export type QuillAnswersState = Record<string, QuillAnswerEntry>;

function stringAnswer(
  answers: QuillAnswersState,
  fieldId: keyof IBusinessCard,
) {
  const raw = answers[fieldId]?.value;
  if (raw === undefined || raw === null) {
    return '';
  }
  return String(raw).trim();
}

export function mapQuillAnswersToBusinessCard(
  answers: QuillAnswersState,
): IBusinessCard {
  const dateStr = stringAnswer(answers, 'date');
  const emailRaw = stringAnswer(answers, 'email');
  const phoneRaw = stringAnswer(answers, 'phoneNumber');
  const websiteRaw = stringAnswer(answers, 'website');

  return {
    name: stringAnswer(answers, 'name'),
    organization: stringAnswer(answers, 'organization'),
    title: stringAnswer(answers, 'title'),
    email:
      emailRaw && isLooseValidEmail(emailRaw)
        ? emailRaw.trim()
        : undefined,
    phoneNumber:
      phoneRaw && isValidPhoneNumber(phoneRaw) ? phoneRaw : undefined,
    website: websiteRaw || undefined,
    notes: stringAnswer(answers, 'notes'),
    date: dateStr || undefined,
  };
}
